import { FilterBaseComponent } from "./filter-base-component.js";
import type { SelectFilter, ColumnData } from "../../types/ListManagerTypes.js";
import { templateParse } from "../../logic/templateParse.js";
import { FILTER_OPERATORS, FilterOperator, getOperatorLabel } from "../../constants/FilterConstants.js";

/**
 * Simple filter component for basic text/number/boolean searches
 * Generates SimpleFilter objects for SQL queries like: title ILIKE '%bonus%'
 */
export class FilterSelectComponent extends FilterBaseComponent {
    static webComponentName: string = "form-select-filter";
    protected _templateId: string = 'select-filter';
     // questa e la lista che verra sempre passata al componente
    // sia che il source sia un array di stringhe o un oggetto verrà trasformata in un array di oggetti
    // con chiavi id e value
    private listOptions: Record<string,any>[] = [];
    // =============================
    // Supported operators for simple filters (from centralized constants)
    // =============================
    private static readonly OPERATORS = FILTER_OPERATORS.select;
    protected selectInput!: HTMLSelectElement;
    protected _operator: FilterOperator = '='; // Default operator
    get operator(): FilterOperator {
        return this._operator;
    }

    set operator(value: FilterOperator) {
        return ; // No operator change allowed for select filters
    }
    // =============================
    // Abstract method implementations
    // =============================
    
    /**
     * Generate SimpleFilter object from current UI state
     */
    generateFilter(): SelectFilter {
        let searchTerm: any;
        
        // Handle different operator types
        if (this.operator === 'IS NULL' || this.operator === 'IS NOT NULL') {
            searchTerm = undefined; // NULL operators don't need search term
        } else if (this.operator === 'BETWEEN') {
            // BETWEEN expects array [min, max]
            searchTerm = Array.isArray(this.filterValue) ? this.filterValue : undefined;
        } else {
            // Select filters always expect string values (from option.value)
            searchTerm = String(this.filterValue || '');
        }
        
        return {
            type: 'select',
            columnName: this.fieldName,
            searchTerm: searchTerm,
            operator: "="
        };
    }
    
    /**
     * Get available operators for simple filters
     */
    getAvailableOperators(): string[] {
        return ['='];
    }
    
    /**
     * Validate filter value based on operator
     */
    validateFilterValue(): boolean {
        return this.filterValue; // Default: accept any non-empty value
    }
   
    private setList(colData: ColumnData):void{
        // Inizializza sempre con array vuoto come fallback
        this.listOptions = [];
        
        const source = colData.filterSelectSource;
        if (!source) {
            console.warn('FilterSelectComponent: No filterSelectSource provided for column', colData.name);
            return;
        }
        
        const settings = colData.filterSelectSettings || {};
        const keyname = settings?.keyname || 'id';
        const valuename = settings?.valuename || 'value';
        
        try {
            if (Array.isArray(source)) {
                if (source.length === 0) {
                    console.warn('FilterSelectComponent: filterSelectSource is empty array for column', colData.name);
                    return;
                }
                
                this.listOptions = source.map((item, index) => {
                    // Gestisce sia array di stringhe che array di oggetti
                    if (typeof item === 'string' || typeof item === 'number') {
                        return {
                            id: item,
                            value: item
                        };
                    } else if (typeof item === 'object' && item !== null) {
                        return {
                            id: item[keyname] || index,
                            value: item[valuename] || String(item)
                        };
                    } else {
                        console.warn('FilterSelectComponent: Invalid item in filterSelectSource array', item);
                        return {
                            id: index,
                            value: String(item)
                        };
                    }
                });
            }
            else if (typeof source === 'object' && source !== null) {
                const entries = Object.entries(source);
                if (entries.length === 0) {
                    console.warn('FilterSelectComponent: filterSelectSource is empty object for column', colData.name);
                    return;
                }
                
                this.listOptions = entries.map(([key, value]) => ({
                    id: key,
                    value: value
                }));
            }
            else {
                console.warn('FilterSelectComponent: Invalid filterSelectSource type, expected array or object, got', typeof source, 'for column', colData.name);
            }
        } catch (error) {
            console.warn('FilterSelectComponent: Error processing filterSelectSource for column', colData.name, error);
            this.listOptions = [];
        }
    }
    protected onPrepareData(data: Record<string, any>): Record<string, any> {
        // Override to add list options to template data
        return {
            ...data,
            listOptions: this.listOptions,
            filterId: this.filterId,
        }
    }
    initialize(colData: ColumnData): void {
        this.setList(colData);
        super.initialize(colData);
    }
    protected afterRender() {
        this.selectInput = this.querySelector('[data-input-source]') as HTMLSelectElement;
    }
    protected setupEventListeners(): void {
        if(this.selectInput) {
            this.selectInput.addEventListener('change', (e: Event) => {
                const target = e.target as HTMLSelectElement;
                if (target.value) {
                    this.filterValue = target.value; // Set the value to the selected option
                    this.dispatchFilterEvent("change");
                } else {
                    console.warn('FilterSelectComponent: No value selected in select input', target);
                }
            });
        }
    }

    
}

// Component registration handled by index.ts